import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { listarDealers as listarDealersApi } from './api/dealers';
import { listarCatalogo } from './api/catalogo';
import {
    listarLeads as listarLeadsApi,
    obtenerLead as obtenerLeadApi,
    crearLead as crearLeadApi,
    crearLeadsMasivo as crearLeadsMasivoApi,
    actualizarLead as actualizarLeadApi,
    actualizarLeadsMasivo as actualizarLeadsMasivoApi,
    eliminarLeadsMasivo as eliminarLeadsMasivoApi,
    agregarNotaLead as agregarNotaLeadApi,
} from './api/leads';
import Sidebar from './Sidebar';
import './GestionLeads.css';
import {
    Upload, Users, Search, Download, Plus, X, ChevronRight,
    Phone, Mail, Car, MapPin, DollarSign, Calendar, Tag,
    FileText, CheckCircle, AlertCircle, TrendingUp, UserCheck, Trash2,
    MessageSquare, Send, ExternalLink, Building2, Activity, Clock,
    ArrowRight, Pencil
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const functions = getFunctions();
const enviarWhatsAppFn = httpsCallable(functions, 'enviarWhatsApp');

const ESTADOS = [
    { key: 'nuevo', label: 'Nuevo', color: '#1e40af', bg: '#dbeafe' },
    { key: 'contactado', label: 'Contactado', color: '#92400e', bg: '#fef3c7' },
    { key: 'calificado', label: 'Calificado', color: '#065f46', bg: '#d1fae5' },
    { key: 'negociando', label: 'Negociando', color: '#5b21b6', bg: '#ede9fe' },
    { key: 'cerrado', label: 'Cerrado', color: '#14532d', bg: '#dcfce7' },
    { key: 'perdido', label: 'Perdido', color: '#991b1b', bg: '#fee2e2' },
];

const LEAD_FIELDS = [
    { key: 'nombre', label: 'Nombre', req: true },
    { key: 'email', label: 'Email', req: true },
    { key: 'telefono', label: 'Telefono', req: false },
    { key: 'modelo', label: 'Modelo de interes', req: false },
    { key: 'presupuesto', label: 'Presupuesto', req: false },
    { key: 'region', label: 'Region', req: false },
    { key: 'concesionario', label: 'Concesionario preferido', req: false },
    { key: 'fuente', label: 'Fuente del lead', req: false },
    { key: 'fecha', label: 'Fecha del lead', req: false },
    { key: 'etapa', label: 'Etapa del funnel', req: false },
    { key: 'notas_ini', label: 'Notas iniciales', req: false },
];

// contentSid: agregar el HXxxxxxxxx de cada template aprobado en Twilio
// Dejar null mientras no estén aprobados — usa texto libre (solo funciona si el cliente escribió primero)
const WA_TEMPLATES = [
    {
        id: 'contacto_inicial',
        label: 'Saludo inicial',
        contentSid: 'HXfd9f80f4c5ec9da9e8d4008cd22d8c9c', // Reemplazar con HXxxxxxxxx una vez aprobado en Twilio
        texto: (l) =>
            `Hola ${l.nombre || ''}! Te contactamos desde DoctorCar. Vimos que tienes interés en ${l.modelo ? 'un ' + l.modelo : 'un vehículo'}. ¿Tienes un momento para conversar?`,
        variables: (l) => ({ '1': l.nombre || '', '2': l.modelo || 'un vehículo' }),
    },
    {
        id: 'doctorcar_cotizacion',
        label: 'Seguimiento cotización',
        contentSid: 'HXf4dac1c04ce2c07430f2ca266d138ccf',
        texto: (l) =>
            `Hola ${l.nombre || ''}! Quería hacer seguimiento a tu consulta sobre el ${l.modelo || 'vehículo'}${l.presupuesto ? '. Tu presupuesto de ' + clpFmt(l.presupuesto) + ' está dentro del rango disponible' : ''}. ¿Tienes alguna pregunta?`,
        variables: (l) => ({ '1': l.nombre || '', '2': l.modelo || 'vehículo', '3': l.presupuesto ? clpFmt(l.presupuesto) : '' }),
    },
    {
        id: 'doctorcar_dealer',
        label: 'Confirmación concesionario',
        contentSid: 'HX2ab002a5b8f1ff1d2b7fc6df2c673d62',
        texto: (l) =>
            `Hola ${l.nombre || ''}! Tu consulta fue enviada a ${l.concesionario || 'nuestro equipo de ventas'}. Ellos te contactarán a la brevedad para coordinar una visita.`,
        variables: (l) => ({ '1': l.nombre || '', '2': l.concesionario || 'nuestro equipo de ventas' }),
    },
    {
        id: 'doctorcar_cierre',
        label: 'Cierre / Oferta',
        contentSid: 'HX6718b04ab1fcaba4bf5e8f3ea5c89258',
        texto: (l) =>
            `Hola ${l.nombre || ''}! Tenemos una oferta especial para el ${l.modelo || 'modelo que te interesó'}. ¿Cuándo podemos agendar una visita al concesionario?`,
        variables: (l) => ({ '1': l.nombre || '', '2': l.modelo || 'el modelo' }),
    },
];

const clpFmt = n =>
    n ? '$' + new Intl.NumberFormat('es-CL').format(Number(String(n).replace(/[^0-9]/g, ''))) : '-';

// Cache de módulo (compartida entre LeadDetailPanel y GestionLeads) para los
// dropdowns marca→modelo→versión — un solo fetch del catálogo completo.
let catalogoCompletoPromise = null;
function obtenerCatalogoCompleto() {
    if (!catalogoCompletoPromise) {
        catalogoCompletoPromise = listarCatalogo({ todos: true }).then(r => r.autos);
    }
    return catalogoCompletoPromise;
}

const fmtDate = ts => {
    if (!ts) return '-';
    const d = ts && ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtDatetime = ts => {
    if (!ts) return '-';
    const d = ts && ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' }) + ' ' +
        d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
};

const initials = nombre => {
    if (!nombre) return '?';
    return nombre.trim().split(' ').slice(0, 2).map(w => (w[0] || '').toUpperCase()).join('');
};

const normalizePhone = tel => {
    if (!tel) return null;
    let n = String(tel).replace(/\D/g, '');
    if (n.startsWith('0')) n = n.slice(1);
    if (!n.startsWith('56') && n.length === 9) n = '56' + n;
    if (!n.startsWith('56') && n.length === 8) n = '569' + n;
    if (n.length < 8) return null;
    return '+' + n;
};

const diasDesde = ts => {
    if (!ts) return null;
    const d = ts && ts.toDate ? ts.toDate() : new Date(ts);
    return Math.floor((Date.now() - d.getTime()) / 86400000);
};

// ─────────────────────────────────────────────────────────────────
// LeadDetailPanel — panel lateral completo con tabs
// ─────────────────────────────────────────────────────────────────
function LeadDetailPanel({ lead: initialLead, dealers, dealersUnicos, onClose, onUpdate }) {
    const [lead, setLead] = useState(initialLead);
    const [panelTab, setPanelTab] = useState('ficha');
    const [editStatus, setEditStatus] = useState(lead.estado || 'nuevo');
    const [dealerAsignar, setDealerAsignar] = useState(lead.dealerAsignado || '');
    const [notaTexto, setNotaTexto] = useState('');
    const [savingNota, setSavingNota] = useState(false);
    const [waMessage, setWaMessage] = useState('');
    const [sendingWA, setSendingWA] = useState(false);
    // Edición inline
    const [editMode, setEditMode] = useState(false);
    const [editForm, setEditForm] = useState({
        nombre: lead.nombre || '',
        email: lead.email || '',
        telefono: lead.telefono || '',
        region: lead.region || '',
        modelo: lead.modelo || '',
        presupuesto: lead.presupuesto || '',
        concesionario: lead.concesionario || '',
        fuente: lead.fuente || '',
        notas_ini: lead.notas_ini || '',
    });
    const [savingEdit, setSavingEdit] = useState(false);
    // Catálogo encadenado para edición
    const [editCatMarcas] = useState([
        "Audi", "BMW", "BYD", "Changan", "Chery", "Chevrolet", "Citroen", "Cupra",
        "Deepal", "DFSK", "Exeed", "Fiat", "Ford", "GAC", "Geely", "GWM", "Honda",
        "Hyundai", "Infiniti", "JAC", "Jeep", "JMC", "Jetour", "Kaiyi", "Karry",
        "Kgm", "Kia", "Lexus", "Lynk & Co", "Mahindra", "Maxus", "Mazda",
        "Mercedes-Benz", "Mg", "Mitsubishi", "Nissan", "Omoda-jaecoo", "Opel",
        "Peugeot", "Ram", "Renault", "Seat", "Skoda", "SsangYong", "Subaru", "Suzuki",
        "Toyota", "Volkswagen", "Volvo",
    ].sort());
    const [editCatMarca, setEditCatMarca] = useState('');
    const [editCatModelos, setEditCatModelos] = useState([]);
    const [editCatModelo, setEditCatModelo] = useState('');
    const [editCatVersiones, setEditCatVersiones] = useState([]);
    const [editCatVersion, setEditCatVersion] = useState('');
    const [editCatLoadingM, setEditCatLoadingM] = useState(false);
    const [editCatLoadingV, setEditCatLoadingV] = useState(false);

    const phoneNorm = normalizePhone(lead.telefono);
    const estadoMeta = ESTADOS.find(e => e.key === editStatus) || ESTADOS[0];
    const dias = diasDesde(lead.actualizadoEn || lead.creadoEn);
    const waHistory = (lead.notas || []).filter(n => n.tipo === 'whatsapp' || n.tipo === 'whatsapp_recibido');
    const notas = (lead.notas || []).filter(n => n.tipo !== 'whatsapp');
    const timeline = [...(lead.notas || [])].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

    const PANEL_TABS = [
        { key: 'ficha', label: 'Ficha', icon: <FileText size={13} /> },
        { key: 'contacto', label: 'Contacto', icon: <Phone size={13} /> },
        { key: 'timeline', label: 'Timeline', icon: <Activity size={13} />, badge: timeline.length },
        { key: 'whatsapp', label: 'WhatsApp', icon: <MessageSquare size={13} />, badge: waHistory.length || null },
    ];

    // Sin listener nativo en Postgres — se hace poll cada 15s mientras el
    // panel está abierto, para capturar respuestas de WhatsApp entrantes
    // sin esperar a que el usuario recargue. Ver plan de migración, Fase 5:
    // este es el único caso de los 4 que sí necesita algo cercano a tiempo real.
    useEffect(() => {
        let cancelado = false;
        const refrescar = async () => {
            try {
                const data = await obtenerLeadApi(initialLead.id);
                if (cancelado) return;
                setLead(data);
                setEditStatus(data.estado || 'nuevo');
                onUpdate(data);
            } catch (e) { console.error('Error refrescando lead:', e); }
        };
        const interval = setInterval(refrescar, 15000);
        return () => { cancelado = true; clearInterval(interval); };
    }, [initialLead.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const syncUpdate = (updated) => {
        setLead(updated);
        onUpdate(updated);
    };

    const guardarEdicion = async () => {
        if (!editForm.nombre.trim() && !editForm.email.trim()) {
            toast.warning('Nombre o email son obligatorios');
            return;
        }
        setSavingEdit(true);
        try {
            const data = {
                nombre: editForm.nombre.trim(),
                email: editForm.email.trim(),
                telefono: editForm.telefono.trim(),
                region: editForm.region.trim(),
                modelo: editForm.modelo.trim(),
                presupuesto: editForm.presupuesto.trim(),
                concesionario: editForm.concesionario.trim(),
                fuente: editForm.fuente.trim(),
                notas_ini: editForm.notas_ini.trim(),
            };
            const actualizado = await actualizarLeadApi(lead.id, data);
            syncUpdate({ ...lead, ...actualizado });
            setEditMode(false);
            toast.success('Lead actualizado');
        } catch (e) { toast.error('Error guardando: ' + e.message); }
        finally { setSavingEdit(false); }
    };

    const cargarModelosEdit = async (marca) => {
        setEditCatMarca(marca);
        setEditCatModelo('');
        setEditCatVersion('');
        setEditCatModelos([]);
        setEditCatVersiones([]);
        if (!marca) return;
        setEditCatLoadingM(true);
        try {
            const catalogo = await obtenerCatalogoCompleto();
            const modelos = [...new Set(catalogo.filter(a => a.marca === marca).map(a => a.model).filter(Boolean))].sort();
            setEditCatModelos(modelos);
        } catch (e) { console.error(e); }
        finally { setEditCatLoadingM(false); }
    };

    const cargarVersionesEdit = async (modelo) => {
        setEditCatModelo(modelo);
        setEditCatVersion('');
        setEditCatVersiones([]);
        if (!modelo) return;
        setEditCatLoadingV(true);
        try {
            const catalogo = await obtenerCatalogoCompleto();
            const seen = new Set();
            const versiones = [];
            catalogo.filter(a => a.marca === editCatMarca && a.model === modelo)
                .forEach(d => { if (d.modelDetail && !seen.has(d.modelDetail)) { seen.add(d.modelDetail); versiones.push(d.modelDetail); } });
            setEditCatVersiones(versiones.sort());
        } catch (e) { console.error(e); }
        finally { setEditCatLoadingV(false); }
    };

    const aplicarCatalogoEdit = () => {
        const modelo = editCatVersion
            ? `${editCatMarca} ${editCatModelo} — ${editCatVersion}`
            : `${editCatMarca} ${editCatModelo}`;
        setEditForm(p => ({ ...p, modelo: modelo.trim() }));
    };

    const actualizarEstado = async (nuevoEstado) => {
        setEditStatus(nuevoEstado);
        try {
            await actualizarLeadApi(lead.id, { estado: nuevoEstado });
            syncUpdate({ ...lead, estado: nuevoEstado });
        } catch { toast.error('Error actualizando estado'); }
    };

    const asignarDealer = async () => {
        try {
            await actualizarLeadApi(lead.id, { dealerAsignado: dealerAsignar });
            syncUpdate({ ...lead, dealerAsignado: dealerAsignar });
            toast.success(dealerAsignar ? 'Asignado a ' + dealerAsignar : 'Asignación removida');
        } catch { toast.error('Error asignando dealer'); }
    };

    const guardarNota = async () => {
        if (!notaTexto.trim()) return;
        setSavingNota(true);
        try {
            const nueva = await agregarNotaLeadApi(lead.id, notaTexto.trim(), 'nota');
            const notas = [...(lead.notas || []), nueva];
            syncUpdate({ ...lead, notas });
            setNotaTexto('');
            toast.success('Nota guardada');
        } catch { toast.error('Error guardando nota'); }
        finally { setSavingNota(false); }
    };

    const [waActiveTpl, setWaActiveTpl] = useState(null); // template seleccionado actualmente

    const enviarWhatsApp = async () => {
        if (!waMessage.trim()) { toast.warning('Escribe un mensaje'); return; }
        if (!phoneNorm) { toast.error('Sin número de teléfono válido'); return; }
        setSendingWA(true);

        // Si el template tiene contentSid aprobado, usarlo para evitar el error 63016
        const tpl = WA_TEMPLATES.find(t => t.id === waActiveTpl);
        const contentSid = tpl?.contentSid || null;
        const contentVariables = contentSid && tpl?.variables
            ? JSON.stringify(tpl.variables(lead))
            : null;

        try {
            const result = await enviarWhatsAppFn({
                to: phoneNorm,
                mensaje: waMessage.trim(),
                leadId: lead.id,
                leadNombre: lead.nombre,
                ...(contentSid ? { contentSid, contentVariables } : {}),
            });
            // enviarWhatsApp ya guarda la nota server-side (atómico con el envío
            // por Twilio) y la devuelve — no hace falta un segundo request.
            const notas = result.data?.nota ? [...(lead.notas || []), result.data.nota] : lead.notas;
            syncUpdate({ ...lead, notas });
            setWaMessage('');
            toast.success('✅ Mensaje enviado por WhatsApp');
        } catch (e) {
            toast.error('Error: ' + (e.message || 'Revisa la configuración de Twilio'));
        } finally { setSendingWA(false); }
    };

    return (
        <div className="leads-detail-overlay" onClick={onClose}>
            <div className="leads-detail-panel leads-detail-panel--wide" onClick={e => e.stopPropagation()}>

                {/* ── Header ── */}
                <div className="leads-detail-header">
                    <div className="leads-detail-avatar">{initials(lead.nombre)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="leads-detail-name">{lead.nombre || '—'}</div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 2 }}>
                            <span style={{
                                background: estadoMeta.bg, color: estadoMeta.color,
                                borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 700
                            }}>{estadoMeta.label}</span>
                            {dias !== null && (
                                <span style={{ fontSize: 11, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 3 }}>
                                    <Clock size={11} /> {dias}d en esta etapa
                                </span>
                            )}
                            {lead.fuente && <span style={{ fontSize: 11, color: '#9ca3af' }}>· {lead.fuente}</span>}
                        </div>
                    </div>
                    <button className="leads-btn leads-btn--ghost leads-btn--sm" onClick={onClose}><X size={16} /></button>
                </div>

                {/* ── Selector de estado rápido ── */}
                <div style={{ padding: '8px 20px 12px', borderBottom: '1px solid #f1f5f9' }}>
                    <div className="leads-status-selector">
                        {ESTADOS.map(e => (
                            <button key={e.key}
                                className={'leads-status-btn' + (editStatus === e.key ? ' active' : '')}
                                style={{ background: e.bg, color: e.color, borderColor: editStatus === e.key ? e.color : 'transparent' }}
                                onClick={() => actualizarEstado(e.key)}>
                                {e.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Sub-tabs ── */}
                <div className="leads-panel-tabs">
                    {PANEL_TABS.map(t => (
                        <button key={t.key}
                            className={'leads-panel-tab' + (panelTab === t.key ? ' active' : '')}
                            onClick={() => setPanelTab(t.key)}>
                            {t.icon} {t.label}
                            {t.badge > 0 && (
                                <span style={{
                                    background: t.key === 'whatsapp' ? '#dcfce7' : '#e0e7ff',
                                    color: t.key === 'whatsapp' ? '#16a34a' : '#3730a3',
                                    borderRadius: 999, fontSize: 10, fontWeight: 700,
                                    padding: '0px 5px', marginLeft: 4,
                                }}>{t.badge}</span>
                            )}
                        </button>
                    ))}
                </div>

                {/* ── Body ── */}
                <div className="leads-detail-body">

                    {/* ══ FICHA ══ */}
                    {panelTab === 'ficha' && (
                        <>
                            {/* Métricas rápidas */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 16 }}>
                                {[
                                    { label: 'Creado', value: fmtDate(lead.creadoEn), icon: <Calendar size={12} /> },
                                    { label: 'Presupuesto', value: lead.presupuesto ? clpFmt(lead.presupuesto) : '—', icon: <DollarSign size={12} /> },
                                    { label: 'Actividades', value: (lead.notas || []).length, icon: <FileText size={12} /> },
                                ].map(({ label, value, icon }) => (
                                    <div key={label} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>{icon} {label}</div>
                                        <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{value}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Botón editar / cancelar */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                                {!editMode
                                    ? <button className="leads-btn leads-btn--sm" onClick={() => { setEditMode(true); setEditForm({ nombre: lead.nombre || '', email: lead.email || '', telefono: lead.telefono || '', region: lead.region || '', modelo: lead.modelo || '', presupuesto: lead.presupuesto || '', concesionario: lead.concesionario || '', fuente: lead.fuente || '', notas_ini: lead.notas_ini || '' }); }}>
                                        <Pencil size={12} /> Editar lead
                                    </button>
                                    : <button className="leads-btn leads-btn--sm" onClick={() => setEditMode(false)} disabled={savingEdit}>
                                        <X size={12} /> Cancelar edición
                                    </button>
                                }
                            </div>

                            {/* ── MODO VER ── */}
                            {!editMode && (<>
                                <div className="leads-detail-section">
                                    <div className="leads-detail-section-title">Contacto</div>
                                    {[
                                        { key: 'email', icon: <Mail size={12} />, label: 'Email' },
                                        { key: 'telefono', icon: <Phone size={12} />, label: 'Teléfono' },
                                        { key: 'region', icon: <MapPin size={12} />, label: 'Región' },
                                    ].filter(f => lead[f.key]).map(f => (
                                        <div key={f.key} className="leads-detail-row">
                                            <div className="leads-detail-key">{f.icon} {f.label}</div>
                                            <div className="leads-detail-val">{lead[f.key]}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="leads-detail-section">
                                    <div className="leads-detail-section-title">Vehículo de interés</div>
                                    {[
                                        { key: 'modelo', icon: <Car size={12} />, label: 'Modelo' },
                                        { key: 'presupuesto', icon: <DollarSign size={12} />, label: 'Presupuesto', fmt: clpFmt },
                                        { key: 'concesionario', icon: <Building2 size={12} />, label: 'Concesionario' },
                                        { key: 'fuente', icon: <Tag size={12} />, label: 'Fuente' },
                                        { key: 'fecha', icon: <Calendar size={12} />, label: 'Fecha lead' },
                                    ].filter(f => lead[f.key]).map(f => (
                                        <div key={f.key} className="leads-detail-row">
                                            <div className="leads-detail-key">{f.icon} {f.label}</div>
                                            <div className="leads-detail-val">{f.fmt ? f.fmt(lead[f.key]) : lead[f.key]}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="leads-detail-section">
                                    <div className="leads-detail-section-title">Concesionario asignado</div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <select className="leads-dealer-select" value={dealerAsignar} onChange={e => setDealerAsignar(e.target.value)}>
                                            <option value="">Sin asignar</option>
                                            {dealers.length > 0
                                                ? dealers.map(d => <option key={d.id} value={d.nombre || d.id}>{d.nombre || d.id}</option>)
                                                : dealersUnicos.map(d => <option key={d} value={d}>{d}</option>)
                                            }
                                        </select>
                                        <button className="leads-btn leads-btn--primary leads-btn--sm"
                                            disabled={dealerAsignar === (lead.dealerAsignado || '')}
                                            onClick={asignarDealer}>
                                            <UserCheck size={13} /> Asignar
                                        </button>
                                    </div>
                                    {lead.dealerAsignado && (
                                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <Building2 size={11} />
                                            <strong>{lead.dealerAsignado}</strong> · asignado el {fmtDate(lead.fechaAsignacion)}
                                        </div>
                                    )}
                                </div>
                            </>)}

                            {/* ── MODO EDITAR ── */}
                            {editMode && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

                                    {/* Contacto */}
                                    <div className="leads-detail-section">
                                        <div className="leads-detail-section-title">Contacto</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                            {[
                                                { key: 'nombre', label: 'Nombre *', ph: 'Juan Pérez', full: true },
                                                { key: 'email', label: 'Email', ph: 'juan@ejemplo.com', full: false },
                                                { key: 'telefono', label: 'Teléfono', ph: '+56 9 1234 5678', full: false },
                                                { key: 'region', label: 'Región', ph: 'Metropolitana', full: false },
                                            ].map(({ key, label, ph, full }) => (
                                                <div key={key} style={{ gridColumn: full ? '1 / -1' : undefined }}>
                                                    <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>{label}</label>
                                                    <input
                                                        type="text"
                                                        placeholder={ph}
                                                        value={editForm[key]}
                                                        onChange={e => setEditForm(p => ({ ...p, [key]: e.target.value }))}
                                                        style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Vehículo con catálogo */}
                                    <div className="leads-detail-section">
                                        <div className="leads-detail-section-title">Vehículo de interés</div>

                                        {/* Selector catálogo */}
                                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', marginBottom: 10 }}>
                                            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8, fontWeight: 600 }}>
                                                <Car size={11} style={{ display: 'inline', marginRight: 4 }} />
                                                Seleccionar del catálogo
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                <select style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, background: '#fff', fontFamily: 'inherit' }}
                                                    value={editCatMarca} onChange={e => cargarModelosEdit(e.target.value)}>
                                                    <option value="">— Marca —</option>
                                                    {editCatMarcas.map(m => <option key={m} value={m}>{m}</option>)}
                                                </select>
                                                {editCatMarca && (
                                                    <select style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, background: '#fff', fontFamily: 'inherit' }}
                                                        value={editCatModelo}
                                                        disabled={editCatLoadingM}
                                                        onChange={e => cargarVersionesEdit(e.target.value)}>
                                                        <option value="">— {editCatLoadingM ? 'Cargando…' : 'Modelo'} —</option>
                                                        {editCatModelos.map(m => <option key={m} value={m}>{m}</option>)}
                                                    </select>
                                                )}
                                                {editCatModelo && (
                                                    <select style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, background: '#fff', fontFamily: 'inherit' }}
                                                        value={editCatVersion}
                                                        disabled={editCatLoadingV}
                                                        onChange={e => setEditCatVersion(e.target.value)}>
                                                        <option value="">— {editCatLoadingV ? 'Cargando…' : 'Versión (opcional)'} —</option>
                                                        {editCatVersiones.map(v => <option key={v} value={v}>{v}</option>)}
                                                    </select>
                                                )}
                                                {editCatModelo && (
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                        <button
                                                            style={{ fontSize: 12, padding: '5px 12px', border: '1px solid #2563eb', borderRadius: 7, background: '#2563eb', cursor: 'pointer', color: '#fff', fontFamily: 'inherit', fontWeight: 600 }}
                                                            onClick={aplicarCatalogoEdit}>
                                                            ✓ Aplicar al campo
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Campos de texto */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                            {[
                                                { key: 'modelo', label: 'Modelo de interés', ph: 'Toyota RAV4', full: true },
                                                { key: 'presupuesto', label: 'Presupuesto', ph: '25000000', full: false },
                                                { key: 'concesionario', label: 'Concesionario', ph: 'Las Condes', full: false },
                                            ].map(({ key, label, ph, full }) => (
                                                <div key={key} style={{ gridColumn: full ? '1 / -1' : undefined }}>
                                                    <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>{label}</label>
                                                    <input
                                                        type="text"
                                                        placeholder={ph}
                                                        value={editForm[key]}
                                                        onChange={e => setEditForm(p => ({ ...p, [key]: e.target.value }))}
                                                        style={{ width: '100%', padding: '7px 10px', border: `1px solid ${key === 'modelo' && editForm[key] ? '#93c5fd' : '#e2e8f0'}`, background: key === 'modelo' && editForm[key] ? '#eff6ff' : '#fff', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                                                    />
                                                </div>
                                            ))}
                                            <div style={{ gridColumn: '1 / -1' }}>
                                                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>Fuente</label>
                                                <select value={editForm.fuente} onChange={e => setEditForm(p => ({ ...p, fuente: e.target.value }))}
                                                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', fontFamily: 'inherit' }}>
                                                    <option value="">Sin fuente</option>
                                                    {['Web', 'Facebook', 'Instagram', 'Google Ads', 'Referido', 'Showroom', 'Llamada', 'Email', 'Otro'].map(f => <option key={f} value={f}>{f}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Botón guardar */}
                                    <div style={{ padding: '12px 0 4px', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                        <button className="leads-btn leads-btn--sm" onClick={() => setEditMode(false)} disabled={savingEdit}>
                                            <X size={12} /> Cancelar
                                        </button>
                                        <button className="leads-btn leads-btn--primary" onClick={guardarEdicion} disabled={savingEdit}>
                                            <CheckCircle size={13} /> {savingEdit ? 'Guardando…' : 'Guardar cambios'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* ══ CONTACTO ══ */}
                    {panelTab === 'contacto' && (
                        <>
                            <div className="leads-detail-section">
                                <div className="leads-detail-section-title">Canales disponibles</div>

                                {/* WhatsApp */}
                                <div className="leads-contact-card">
                                    <div className="leads-contact-card-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
                                        <MessageSquare size={18} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600, fontSize: 13 }}>WhatsApp</div>
                                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                                            {phoneNorm || 'Sin teléfono registrado'}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        {phoneNorm && (
                                            <a href={`https://wa.me/${phoneNorm.replace('+', '')}?text=${encodeURIComponent('Hola ' + (lead.nombre || '') + ', te contactamos desde DoctorCar.')}`}
                                                target="_blank" rel="noopener noreferrer"
                                                className="leads-btn leads-btn--sm"
                                                style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                <ExternalLink size={12} /> WA Web
                                            </a>
                                        )}
                                        <button className="leads-btn leads-btn--primary leads-btn--sm"
                                            onClick={() => setPanelTab('whatsapp')}>
                                            <Send size={12} /> API Twilio
                                        </button>
                                    </div>
                                </div>

                                {/* Email */}
                                {lead.email && (
                                    <div className="leads-contact-card">
                                        <div className="leads-contact-card-icon" style={{ background: '#dbeafe', color: '#1d4ed8' }}>
                                            <Mail size={18} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 600, fontSize: 13 }}>Email</div>
                                            <div style={{ fontSize: 12, color: '#6b7280' }}>{lead.email}</div>
                                        </div>
                                        <a href={`mailto:${lead.email}?subject=DoctorCar - ${lead.modelo || 'Consulta'}&body=Hola ${lead.nombre || ''},`}
                                            className="leads-btn leads-btn--sm"
                                            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                            <ExternalLink size={12} /> Abrir
                                        </a>
                                    </div>
                                )}

                                {/* Teléfono */}
                                {lead.telefono && (
                                    <div className="leads-contact-card">
                                        <div className="leads-contact-card-icon" style={{ background: '#f3f4f6', color: '#374151' }}>
                                            <Phone size={18} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 600, fontSize: 13 }}>Teléfono</div>
                                            <div style={{ fontSize: 12, color: '#6b7280' }}>{lead.telefono}</div>
                                        </div>
                                        <a href={`tel:${lead.telefono}`}
                                            className="leads-btn leads-btn--sm"
                                            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                            <Phone size={12} /> Llamar
                                        </a>
                                    </div>
                                )}
                            </div>

                            {/* Resumen copiable */}
                            <div className="leads-detail-section">
                                <div className="leads-detail-section-title">Resumen del lead</div>
                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, fontSize: 12, color: '#374151', lineHeight: 1.7 }}>
                                    <strong>{lead.nombre}</strong> está interesado en
                                    {lead.modelo ? <> un <strong>{lead.modelo}</strong></> : ' un vehículo'}
                                    {lead.presupuesto ? <> · presupuesto <strong>{clpFmt(lead.presupuesto)}</strong></> : ''}
                                    {lead.region ? <> · {lead.region}</> : ''}
                                    {lead.concesionario ? <> · prefiere <strong>{lead.concesionario}</strong></> : ''}
                                    {lead.fuente ? <> · fuente: {lead.fuente}</> : ''}.
                                </div>
                            </div>
                        </>
                    )}

                    {/* ══ TIMELINE ══ */}
                    {panelTab === 'timeline' && (
                        <>
                            <div className="leads-detail-section">
                                <div className="leads-detail-section-title">Nueva nota</div>
                                <textarea
                                    className="leads-nota-input"
                                    placeholder="Escribe una nota de seguimiento… (Ctrl+Enter para guardar)"
                                    value={notaTexto}
                                    onChange={e => setNotaTexto(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) guardarNota(); }}
                                />
                                <button className="leads-btn leads-btn--primary leads-btn--sm"
                                    style={{ marginTop: 8 }}
                                    disabled={!notaTexto.trim() || savingNota}
                                    onClick={guardarNota}>
                                    <FileText size={13} /> {savingNota ? 'Guardando…' : 'Guardar nota'}
                                </button>
                            </div>

                            <div className="leads-detail-section">
                                <div className="leads-detail-section-title">
                                    Historial · {timeline.length} evento{timeline.length !== 1 ? 's' : ''}
                                </div>
                                {timeline.length === 0
                                    ? <p style={{ fontSize: 13, color: '#94a3b8' }}>Sin actividad registrada.</p>
                                    : (
                                        <div className="leads-timeline">
                                            {timeline.map((item, i) => {
                                                const esWA = item.tipo === 'whatsapp';
                                                const esWARecv = item.tipo === 'whatsapp_recibido';
                                                return (
                                                    <div key={i} className="leads-timeline-item">
                                                        <div className="leads-timeline-dot"
                                                            style={{ background: esWA ? '#16a34a' : esWARecv ? '#0891b2' : '#2563eb' }} />
                                                        <div className="leads-timeline-content">
                                                            {esWA && (
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#16a34a', fontWeight: 700, marginBottom: 2 }}>
                                                                    <MessageSquare size={10} /> WhatsApp enviado
                                                                </div>
                                                            )}
                                                            {esWARecv && (
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#0891b2', fontWeight: 700, marginBottom: 2 }}>
                                                                    <MessageSquare size={10} /> ← Respuesta del cliente
                                                                </div>
                                                            )}
                                                            <div className="leads-timeline-text">{item.texto}</div>
                                                            <div className="leads-timeline-meta">
                                                                {item.autor} · {item.fecha ? fmtDatetime(item.fecha) : ''}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )
                                }
                            </div>
                        </>
                    )}

                    {/* ══ WHATSAPP ══ */}
                    {panelTab === 'whatsapp' && (
                        <>
                            {/* Estado conexión */}
                            <div style={{
                                background: phoneNorm ? '#f0fdf4' : '#fef2f2',
                                border: `1px solid ${phoneNorm ? '#bbf7d0' : '#fecaca'}`,
                                borderRadius: 10, padding: '12px 14px', marginBottom: 12,
                                display: 'flex', alignItems: 'center', gap: 12,
                            }}>
                                <MessageSquare size={20} style={{ color: phoneNorm ? '#16a34a' : '#dc2626', flexShrink: 0 }} />
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 13, color: phoneNorm ? '#166534' : '#991b1b' }}>
                                        {phoneNorm ? `Listo para enviar a ${phoneNorm}` : '⚠️ Sin número de teléfono'}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
                                        {phoneNorm
                                            ? 'Vía Twilio WhatsApp API · los mensajes quedan registrados en el timeline'
                                            : 'Agrega un teléfono en la ficha para habilitar WhatsApp'}
                                    </div>
                                </div>
                            </div>

                            {/* Plantillas */}
                            <div className="leads-detail-section">
                                <div className="leads-detail-section-title">Plantillas</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {WA_TEMPLATES.map(t => (
                                        <button key={t.id} className="leads-template-btn"
                                            onClick={() => { setWaMessage(t.texto(lead)); setWaActiveTpl(t.id); }}>
                                            <div style={{ flex: 1, textAlign: 'left' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <span style={{ fontWeight: 600, fontSize: 12, color: '#374151' }}>{t.label}</span>
                                                    {t.contentSid
                                                        ? <span style={{ fontSize: 10, background: '#dcfce7', color: '#166534', borderRadius: 4, padding: '1px 5px', fontWeight: 600 }}>aprobado</span>
                                                        : <span style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '1px 5px' }}>solo en ventana 24h</span>
                                                    }
                                                </div>
                                                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                                                    {t.texto(lead).slice(0, 80)}…
                                                </div>
                                            </div>
                                            <ArrowRight size={14} style={{ color: '#cbd5e1', flexShrink: 0 }} />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Editor */}
                            <div className="leads-detail-section">
                                <div className="leads-detail-section-title">Mensaje</div>
                                <textarea
                                    className="leads-nota-input"
                                    placeholder="Escribe o personaliza el mensaje…"
                                    value={waMessage}
                                    onChange={e => setWaMessage(e.target.value)}
                                    style={{ minHeight: 110 }}
                                />
                                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                                    <span style={{ fontSize: 11, color: '#9ca3af', flex: 1 }}>{waMessage.length} caracteres</span>
                                    {phoneNorm && (
                                        <a href={`https://wa.me/${phoneNorm.replace('+', '')}?text=${encodeURIComponent(waMessage)}`}
                                            target="_blank" rel="noopener noreferrer"
                                            className="leads-btn leads-btn--sm"
                                            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                            <ExternalLink size={12} /> WA Web (sin API)
                                        </a>
                                    )}
                                    <button className="leads-btn leads-btn--success"
                                        disabled={!waMessage.trim() || !phoneNorm || sendingWA}
                                        onClick={enviarWhatsApp}>
                                        <Send size={13} /> {sendingWA ? 'Enviando…' : 'Enviar vía Twilio'}
                                    </button>
                                </div>
                            </div>

                            {/* Historial WA */}
                            {waHistory.length > 0 && (
                                <div className="leads-detail-section">
                                    <div className="leads-detail-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span>Conversación</span>
                                        <span style={{ fontSize: 11, fontWeight: 400, color: '#6b7280' }}>
                                            {waHistory.filter(m => m.tipo === 'whatsapp').length} enviados
                                            {waHistory.filter(m => m.tipo === 'whatsapp_recibido').length > 0 &&
                                                <span style={{ color: '#0891b2', fontWeight: 600 }}> · {waHistory.filter(m => m.tipo === 'whatsapp_recibido').length} recibidos</span>
                                            }
                                        </span>
                                    </div>
                                    <div className="leads-timeline">
                                        {[...waHistory].reverse().map((m, i) => {
                                            const recibido = m.tipo === 'whatsapp_recibido';
                                            return (
                                                <div key={i} className="leads-timeline-item">
                                                    <div className="leads-timeline-dot"
                                                        style={{ background: recibido ? '#0891b2' : '#16a34a' }} />
                                                    <div className="leads-timeline-content"
                                                        style={{ background: recibido ? '#f0f9ff' : undefined, borderRadius: recibido ? 8 : undefined, padding: recibido ? '6px 8px' : undefined }}>
                                                        <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 3, color: recibido ? '#0891b2' : '#16a34a' }}>
                                                            <MessageSquare size={10} />
                                                            {recibido ? '← Respuesta del cliente' : `Enviado · ${m.autor}`}
                                                            {m.sid && <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 4 }}>·{m.sid.slice(-8)}</span>}
                                                        </div>
                                                        <div className="leads-timeline-text">{m.texto}</div>
                                                        <div className="leads-timeline-meta">{fmtDatetime(m.fecha)}</div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* ── Footer ── */}
                <div className="leads-detail-footer">
                    <button className="leads-btn" style={{ flex: 1 }} onClick={onClose}>Cerrar</button>
                    {panelTab === 'ficha' && editMode && (
                        <button className="leads-btn leads-btn--primary" style={{ flex: 2 }}
                            disabled={savingEdit}
                            onClick={guardarEdicion}>
                            <CheckCircle size={14} /> {savingEdit ? 'Guardando…' : 'Guardar cambios'}
                        </button>
                    )}
                    {panelTab === 'timeline' && (
                        <button className="leads-btn leads-btn--primary" style={{ flex: 2 }}
                            disabled={!notaTexto.trim() || savingNota}
                            onClick={guardarNota}>
                            <FileText size={14} /> {savingNota ? 'Guardando…' : 'Guardar nota'}
                        </button>
                    )}
                    {panelTab === 'whatsapp' && (
                        <button className="leads-btn leads-btn--success" style={{ flex: 2 }}
                            disabled={!waMessage.trim() || !phoneNorm || sendingWA}
                            onClick={enviarWhatsApp}>
                            <Send size={14} /> {sendingWA ? 'Enviando…' : 'Enviar WhatsApp'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────
function GestionLeads({ embedded = false }) {
    const [activeTab, setActiveTab] = useState('todos');
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('');
    const [filtroFuente, setFiltroFuente] = useState('');
    const [filtroDealer, setFiltroDealer] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [leadActivo, setLeadActivo] = useState(null);
    const [showAccionesMasivas, setShowAccionesMasivas] = useState(false);
    const [accionMasiva, setAccionMasiva] = useState(''); // 'dealer' | 'estado' | 'whatsapp'
    const [masivoDealerVal, setMasivoDealerVal] = useState('');
    const [masivoEstadoVal, setMasivoEstadoVal] = useState('');
    const [masivoWaTemplate, setMasivoWaTemplate] = useState('');
    const [masivoWaMensaje, setMasivoWaMensaje] = useState('');
    const [masivoRunning, setMasivoRunning] = useState(false);
    const [masivoResultado, setMasivoResultado] = useState(null);
    const [dealers, setDealers] = useState([]);
    const [showImport, setShowImport] = useState(false);
    const [showNuevoLead, setShowNuevoLead] = useState(false);
    const [nuevoLead, setNuevoLead] = useState({});
    const [savingLead, setSavingLead] = useState(false);
    const [excelData, setExcelData] = useState(null);
    const [excelHeaders, setExcelHeaders] = useState([]);
    const [colMap, setColMap] = useState({});
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    // Catálogo encadenado (nuevo lead)
    const [catMarcas, setCatMarcas] = useState([]);
    const [catModelos, setCatModelos] = useState([]);
    const [catVersiones, setCatVersiones] = useState([]);
    const [catMarcaSel, setCatMarcaSel] = useState('');
    const [catModeloSel, setCatModeloSel] = useState('');
    const [catVersionSel, setCatVersionSel] = useState('');
    const [catLoadingModelos, setCatLoadingModelos] = useState(false);
    const [catLoadingVersiones, setCatLoadingVersiones] = useState(false);

    useEffect(() => {
        cargarLeads();
        cargarDealers();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    async function cargarLeads() {
        setLoading(true);
        try {
            setLeads(await listarLeadsApi());
        } catch (e) { console.error(e); toast.error('Error cargando leads'); }
        finally { setLoading(false); }
    }

    async function cargarDealers() {
        try {
            setDealers(await listarDealersApi());
        } catch { setDealers([]); }
    }

    // ── Catálogo encadenado ──────────────────────────────────────
    useEffect(() => {
        // Cargar marcas únicas desde 'fechas' no es necesario —
        // usamos MARCAS_CONOCIDAS del catálogo directamente
        const MARCAS = [
            "Toyota", "Nissan", "Peugeot", "Citroen", "Ram", "Chery", "Mg", "Lexus",
            "Hyundai", "Opel", "Jeep", "Fiat", "Exeed", "Omoda-jaecoo", "Mazda",
            "Subaru", "Deepal", "JAC", "Changan", "Renault", "GWM", "Suzuki", "Geely",
            "DFSK", "Infiniti", "Kia", "BMW", "Mercedes-Benz", "Volvo", "Honda", "BYD",
            "GAC", "JMC", "Kgm", "SsangYong", "Chevrolet", "Cupra", "Seat", "Skoda",
            "Audi", "Volkswagen", "Kaiyi", "Karry", "Maxus", "Mitsubishi", "Jetour",
            "Ford", "Lynk & Co", "Mahindra",
        ];
        setCatMarcas(MARCAS.sort());
    }, []);

    const cargarModelosCat = async (marca) => {
        setCatMarcaSel(marca);
        setCatModeloSel('');
        setCatVersionSel('');
        setCatModelos([]);
        setCatVersiones([]);
        if (!marca) return;
        setCatLoadingModelos(true);
        try {
            const catalogo = await obtenerCatalogoCompleto();
            const modelos = [...new Set(catalogo.filter(a => a.marca === marca).map(a => a.model).filter(Boolean))].sort();
            setCatModelos(modelos);
        } catch (e) { console.error(e); toast.error('Error cargando modelos'); }
        finally { setCatLoadingModelos(false); }
    };

    const cargarVersionesCat = async (marca, modelo) => {
        setCatModeloSel(modelo);
        setCatVersionSel('');
        setCatVersiones([]);
        if (!modelo) return;
        setCatLoadingVersiones(true);
        try {
            const catalogo = await obtenerCatalogoCompleto();
            const seen = new Set();
            const versiones = [];
            catalogo.filter(a => a.marca === marca && a.model === modelo).forEach(d => {
                const v = d.modelDetail;
                if (v && !seen.has(v)) { seen.add(v); versiones.push(v); }
            });
            setCatVersiones(versiones.sort());
        } catch (e) { console.error(e); toast.error('Error cargando versiones'); }
        finally { setCatLoadingVersiones(false); }
    };

    const aplicarSeleccionCatalogo = () => {
        // Construye el string del modelo como "Marca Modelo (Versión)"
        const partes = [catMarcaSel, catModeloSel, catVersionSel].filter(Boolean);
        const modeloStr = catVersionSel
            ? `${catMarcaSel} ${catModeloSel} — ${catVersionSel}`
            : `${catMarcaSel} ${catModeloSel}`;
        setNuevoLead(p => ({ ...p, modelo: modeloStr.trim() }));
    };

    const limpiarSeleccionCatalogo = () => {
        setCatMarcaSel(''); setCatModeloSel(''); setCatVersionSel('');
        setCatModelos([]); setCatVersiones([]);
    };

    const leadsFiltrados = useMemo(() => {
        const term = search.trim().toLowerCase();
        return leads.filter(l => {
            if (activeTab !== 'todos' && l.estado !== activeTab) return false;
            if (filtroEstado && l.estado !== filtroEstado) return false;
            if (filtroFuente && l.fuente !== filtroFuente) return false;
            if (filtroDealer === '__none__' && l.dealerAsignado) return false;
            if (filtroDealer && filtroDealer !== '__none__' && l.dealerAsignado !== filtroDealer) return false;
            if (term) {
                const haystack = [l.nombre, l.email, l.telefono, l.modelo, l.region].join(' ').toLowerCase();
                if (!haystack.includes(term)) return false;
            }
            return true;
        });
    }, [leads, activeTab, search, filtroEstado, filtroFuente, filtroDealer]);

    const conteos = useMemo(() => {
        const m = { todos: leads.length };
        ESTADOS.forEach(e => { m[e.key] = leads.filter(l => l.estado === e.key).length; });
        return m;
    }, [leads]);

    const kpis = useMemo(() => ({
        total: leads.length,
        nuevos: leads.filter(l => l.estado === 'nuevo').length,
        sinAsignar: leads.filter(l => !l.dealerAsignado).length,
        cerrados: leads.filter(l => l.estado === 'cerrado').length,
        tasa: leads.length ? ((leads.filter(l => l.estado === 'cerrado').length / leads.length) * 100).toFixed(1) : '0',
    }), [leads]);

    const dealersUnicos = useMemo(() => [...new Set(leads.map(l => l.dealerAsignado).filter(Boolean))].sort(), [leads]);
    const fuentesUnicas = useMemo(() => [...new Set(leads.map(l => l.fuente).filter(Boolean))].sort(), [leads]);

    const onLeadUpdate = updated => {
        setLeads(prev => prev.map(l => l.id === updated.id ? updated : l));
        setLeadActivo(updated);
    };

    async function eliminarSeleccionados() {
        if (!selectedIds.size) return;
        if (!window.confirm('¿Eliminar ' + selectedIds.size + ' lead(s)?')) return;
        await eliminarLeadsMasivoApi([...selectedIds]);
        setLeads(prev => prev.filter(l => !selectedIds.has(l.id)));
        setSelectedIds(new Set());
        toast.success(selectedIds.size + ' lead(s) eliminados');
    }

    function exportarExcel() {
        const data = leadsFiltrados.map(l => ({
            Nombre: l.nombre || '', Email: l.email || '', Telefono: l.telefono || '',
            Modelo: l.modelo || '', Presupuesto: l.presupuesto || '', Region: l.region || '',
            Concesionario: l.concesionario || '', Fuente: l.fuente || '', Estado: l.estado || '',
            'Dealer asignado': l.dealerAsignado || '', 'Fecha creacion': fmtDate(l.creadoEn),
            Notas: (l.notas || []).filter(n => n.tipo !== 'whatsapp').map(n => n.texto).join(' | '),
            'WA enviados': (l.notas || []).filter(n => n.tipo === 'whatsapp').length,
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Leads');
        XLSX.writeFile(wb, 'leads_' + new Date().toISOString().slice(0, 10) + '.xlsx');
    }

    function procesarExcel(file) {
        const reader = new FileReader();
        reader.onload = function (e) {
            try {
                const wb = XLSX.read(e.target.result, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
                if (!rows.length) { toast.error('Archivo vacío'); return; }
                const headers = rows[0].map(h => String(h).trim());
                const dataRows = rows.slice(1).filter(r => r.some(c => c !== ''));
                setExcelHeaders(headers);
                setExcelData(dataRows);
                const autoMap = {};
                LEAD_FIELDS.forEach(function (field) {
                    const idx = headers.findIndex(function (h) {
                        const hn = h.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                        const fn = field.label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                        return hn.includes(fn.split(' ')[0]) || fn.includes(hn.split(' ')[0]);
                    });
                    if (idx >= 0) autoMap[field.key] = idx;
                });
                setColMap(autoMap);
            } catch (err) { toast.error('Error procesando Excel'); console.error(err); }
        };
        reader.readAsBinaryString(file);
    }

    function handleFileDrop(e) {
        e.preventDefault(); setDragOver(false);
        const file = (e.dataTransfer && e.dataTransfer.files[0]) || (e.target && e.target.files && e.target.files[0]);
        if (file) procesarExcel(file);
    }

    async function confirmarImportacion() {
        if (!excelData || !excelData.length) return;
        setImporting(true); setImportProgress(0);
        try {
            const batch = [];
            excelData.forEach(function (row) {
                const lead = {};
                LEAD_FIELDS.forEach(function (field) {
                    const idx = colMap[field.key];
                    if (idx !== undefined && row[idx] !== '') lead[field.key] = String(row[idx]).trim();
                });
                if (lead.nombre || lead.email) batch.push(lead);
            });
            setImportProgress(50);
            const importados = await crearLeadsMasivoApi(batch);
            setImportProgress(100);
            toast.success(importados + ' leads importados');
            setShowImport(false); setExcelData(null); setExcelHeaders([]); setColMap({});
            await cargarLeads();
        } catch (err) { console.error(err); toast.error('Error en la importación'); }
        finally { setImporting(false); setImportProgress(0); }
    }

    function toggleSelectAll() {
        if (selectedIds.size === leadsFiltrados.length) setSelectedIds(new Set());
        else setSelectedIds(new Set(leadsFiltrados.map(l => l.id)));
    }
    function toggleSelect(id) {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelectedIds(next);
    }
    function descargarPlantilla() {
        const ws = XLSX.utils.aoa_to_sheet([LEAD_FIELDS.map(f => f.label)]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Leads');
        XLSX.writeFile(wb, 'plantilla_leads.xlsx');
    }
    async function guardarNuevoLead() {
        if (!nuevoLead.nombre && !nuevoLead.email) { toast.error('Nombre o email requeridos'); return; }
        setSavingLead(true);
        try {
            await crearLeadApi({ ...nuevoLead, estado: nuevoLead.estado || 'nuevo' });
            toast.success('Lead creado'); setShowNuevoLead(false); setNuevoLead({});
            await cargarLeads();
        } catch (e) { console.error(e); toast.error('Error creando lead'); }
        finally { setSavingLead(false); }
    }

    // ── Acciones masivas ─────────────────────────────────────────
    const ejecutarAccionMasiva = async () => {
        const leadsSeleccionados = leads.filter(l => selectedIds.has(l.id));
        if (!leadsSeleccionados.length) return;

        setMasivoRunning(true);
        setMasivoResultado(null);
        let ok = 0, err = 0, omitidos = 0;

        try {
            if (accionMasiva === 'dealer') {
                if (!masivoDealerVal && masivoDealerVal !== '') { toast.warning('Selecciona un concesionario'); setMasivoRunning(false); return; }
                ok = await actualizarLeadsMasivoApi([...selectedIds], { dealerAsignado: masivoDealerVal });
                setLeads(prev => prev.map(l =>
                    selectedIds.has(l.id) ? { ...l, dealerAsignado: masivoDealerVal } : l
                ));

            } else if (accionMasiva === 'estado') {
                if (!masivoEstadoVal) { toast.warning('Selecciona un estado'); setMasivoRunning(false); return; }
                ok = await actualizarLeadsMasivoApi([...selectedIds], { estado: masivoEstadoVal });
                setLeads(prev => prev.map(l =>
                    selectedIds.has(l.id) ? { ...l, estado: masivoEstadoVal } : l
                ));

            } else if (accionMasiva === 'whatsapp') {
                if (!masivoWaMensaje.trim()) { toast.warning('Escribe un mensaje'); setMasivoRunning(false); return; }

                const tpl = WA_TEMPLATES.find(t => t.id === masivoWaTemplate);
                const usarTemplate = !!tpl?.contentSid;

                for (const lead of leadsSeleccionados) {
                    const phoneNorm = normalizePhone(lead.telefono);
                    if (!phoneNorm) { omitidos++; continue; }

                    try {
                        const contentSid = usarTemplate ? tpl.contentSid : null;
                        const contentVariables = usarTemplate && tpl.variables
                            ? JSON.stringify(tpl.variables(lead))
                            : null;

                        // Personalizar mensaje con datos del lead
                        const mensajePersonalizado = usarTemplate
                            ? masivoWaMensaje
                            : masivoWaMensaje
                                .replace(/\{nombre\}/gi, lead.nombre || '')
                                .replace(/\{modelo\}/gi, lead.modelo || '')
                                .replace(/\{concesionario\}/gi, lead.concesionario || '');

                        const result = await enviarWhatsAppFn({
                            to: phoneNorm,
                            mensaje: mensajePersonalizado,
                            leadId: lead.id,
                            leadNombre: lead.nombre,
                            masivo: true,
                            ...(contentSid ? { contentSid, contentVariables } : {}),
                        });

                        // enviarWhatsApp ya guarda la nota server-side — solo reflejarla localmente
                        if (result.data?.nota) {
                            const notas = [...(lead.notas || []), result.data.nota];
                            setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, notas } : l));
                        }
                        ok++;

                        // Pequeña pausa para no saturar la API
                        await new Promise(r => setTimeout(r, 300));
                    } catch { err++; }
                }
            }

            setMasivoResultado({ ok, err, omitidos, accion: accionMasiva });
            if (err === 0) {
                toast.success(`✅ ${ok} leads actualizados`);
                if (accionMasiva !== 'whatsapp') {
                    setSelectedIds(new Set());
                    setShowAccionesMasivas(false);
                }
            } else {
                toast.error(`⚠️ ${ok} OK · ${err} errores`);
            }
        } catch (e) {
            toast.error('Error en la acción masiva: ' + e.message);
        } finally {
            setMasivoRunning(false);
        }
    };

    const innerContent = (
        <div className="leads-main">
            <div className="leads-wrapper">

                {/* Encabezado */}
                <div className="leads-page-header">
                    <div>
                        <h1 className="leads-page-title">Gestión de Leads</h1>
                        <p className="leads-page-sub">Administra, contacta y haz seguimiento · WhatsApp via Twilio</p>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button className="leads-btn" onClick={exportarExcel} disabled={!leads.length}><Download size={14} /> Exportar</button>
                        <button className="leads-btn" onClick={() => setShowNuevoLead(true)}><Plus size={14} /> Nuevo lead</button>
                        <button className="leads-btn leads-btn--primary" onClick={() => setShowImport(true)}><Upload size={14} /> Importar Excel</button>
                    </div>
                </div>

                {/* KPIs */}
                <div className="leads-kpis">
                    {[
                        { label: 'Total leads', value: kpis.total, icon: <Users size={15} />, color: '#2563eb' },
                        { label: 'Nuevos', value: kpis.nuevos, icon: <Plus size={15} />, color: '#1e40af' },
                        { label: 'Sin asignar', value: kpis.sinAsignar, icon: <AlertCircle size={15} />, color: '#d97706' },
                        { label: 'Cerrados', value: kpis.cerrados, icon: <CheckCircle size={15} />, color: '#059669' },
                        { label: 'Tasa de cierre', value: kpis.tasa + '%', icon: <TrendingUp size={15} />, color: '#7c3aed' },
                    ].map(({ label, value, icon, color }) => (
                        <div key={label} className="leads-kpi">
                            <div className="leads-kpi-accent" style={{ background: color }} />
                            <div style={{ background: color + '20', color, borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>{icon}</div>
                            <div className="leads-kpi-val">{value}</div>
                            <div className="leads-kpi-label">{label}</div>
                        </div>
                    ))}
                </div>

                {/* Import */}
                {showImport && (
                    <div className="leads-card">
                        <div className="leads-card-header">
                            <div className="leads-card-icon" style={{ background: '#eff6ff', color: '#2563eb' }}><Upload size={14} /></div>
                            <span className="leads-card-title">Importar leads desde Excel</span>
                            <button className="leads-btn leads-btn--ghost leads-btn--sm" onClick={() => { setShowImport(false); setExcelData(null); setExcelHeaders([]); setColMap({}); }}><X size={14} /></button>
                        </div>
                        <div className="leads-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {!excelData ? (
                                <div>
                                    <div className={dragOver ? 'leads-upload-zone drag-over' : 'leads-upload-zone'}
                                        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={handleFileDrop}
                                        onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                                        <div className="leads-upload-icon">📊</div>
                                        <div className="leads-upload-title">Arrastra tu archivo Excel aquí</div>
                                        <div className="leads-upload-sub">o haz click para seleccionar · .xlsx .xls .csv</div>
                                        <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleFileDrop} />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                        <FileText size={14} style={{ color: '#64748b' }} />
                                        <span style={{ fontSize: 13, color: '#64748b', flex: 1 }}>Descarga la plantilla con las columnas correctas</span>
                                        <button className="leads-btn leads-btn--sm" onClick={descargarPlantilla}><Download size={12} /> Plantilla</button>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <CheckCircle size={16} style={{ color: '#059669' }} />
                                        <span style={{ fontSize: 13, fontWeight: 600 }}>{excelData.length} filas detectadas</span>
                                        <button className="leads-btn leads-btn--ghost leads-btn--sm" onClick={() => { setExcelData(null); setExcelHeaders([]); setColMap({}); }}>Cambiar archivo</button>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>Mapear columnas</div>
                                        <div className="leads-mapper-grid">
                                            {LEAD_FIELDS.map(field => (
                                                <div key={field.key} className="leads-mapper-field">
                                                    <div className="leads-mapper-label">{field.label}{field.req && <span className="leads-mapper-req">*</span>}</div>
                                                    <select className="leads-mapper-select" value={colMap[field.key] !== undefined ? colMap[field.key] : ''} onChange={e => { const val = e.target.value; setColMap(p => { const next = { ...p }; if (val === '') delete next[field.key]; else next[field.key] = Number(val); return next; }); }}>
                                                        <option value="">No mapear</option>
                                                        {excelHeaders.map((h, i) => <option key={i} value={i}>{h || 'Columna ' + (i + 1)}</option>)}
                                                    </select>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Vista previa</div>
                                        <div className="leads-preview-wrap">
                                            <table className="leads-preview-table">
                                                <thead><tr>{excelHeaders.map((h, i) => <th key={i}>{h || 'Col ' + (i + 1)}</th>)}</tr></thead>
                                                <tbody>{excelData.slice(0, 5).map((row, ri) => <tr key={ri}>{excelHeaders.map((_, ci) => <td key={ci}>{String(row[ci] !== undefined ? row[ci] : '')}</td>)}</tr>)}</tbody>
                                            </table>
                                        </div>
                                    </div>
                                    {importing && (
                                        <div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 6 }}>
                                                <span>Importando...</span><span>{importProgress}%</span>
                                            </div>
                                            <div className="leads-progress-bar"><div className="leads-progress-fill" style={{ width: importProgress + '%' }} /></div>
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                        <button className="leads-btn" onClick={() => { setExcelData(null); setExcelHeaders([]); setColMap({}); }}>Cancelar</button>
                                        <button className="leads-btn leads-btn--success" onClick={confirmarImportacion} disabled={importing}><Upload size={14} /> {importing ? 'Importando...' : 'Importar ' + excelData.length + ' leads'}</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="leads-tabs">
                    <button className={activeTab === 'todos' ? 'leads-tab active' : 'leads-tab'} onClick={() => setActiveTab('todos')}>
                        <Users size={14} /> Todos <span className="leads-tab-count">{conteos.todos}</span>
                    </button>
                    {ESTADOS.map(e => (
                        <button key={e.key} className={activeTab === e.key ? 'leads-tab active' : 'leads-tab'} onClick={() => setActiveTab(e.key)}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: e.color, display: 'inline-block' }} />
                            {e.label} <span className="leads-tab-count">{conteos[e.key] || 0}</span>
                        </button>
                    ))}
                </div>

                {/* Tabla */}
                <div className="leads-card">
                    <div className="leads-card-header">
                        <div style={{ display: 'flex', gap: 8, flex: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <div className="leads-search-wrap" style={{ maxWidth: 280 }}>
                                <span className="leads-search-icon"><Search size={14} /></span>
                                <input className="leads-search-input" placeholder="Buscar nombre, email, modelo..." value={search} onChange={e => setSearch(e.target.value)} />
                            </div>
                            <select className="leads-filter-select" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
                                <option value="">Todos los estados</option>
                                {ESTADOS.map(e => <option key={e.key} value={e.key}>{e.label}</option>)}
                            </select>
                            <select className="leads-filter-select" value={filtroFuente} onChange={e => setFiltroFuente(e.target.value)}>
                                <option value="">Todas las fuentes</option>
                                {fuentesUnicas.map(f => <option key={f} value={f}>{f}</option>)}
                            </select>
                            {dealersUnicos.length > 0 && (
                                <select className="leads-filter-select" value={filtroDealer} onChange={e => setFiltroDealer(e.target.value)}>
                                    <option value="">Todos los dealers</option>
                                    <option value="__none__">Sin asignar</option>
                                    {dealersUnicos.map(d => <option key={d} value={d}>{d}</option>)}
                                </select>
                            )}
                            <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>{leadsFiltrados.length} leads</span>
                            {selectedIds.size > 0 && (
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 4 }}>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '3px 8px' }}>
                                        {selectedIds.size} seleccionados
                                    </span>
                                    <button className="leads-btn leads-btn--sm" style={{ background: '#16a34a', color: '#fff', borderColor: '#16a34a' }}
                                        onClick={() => { setAccionMasiva('whatsapp'); setMasivoWaTemplate(''); setMasivoWaMensaje(''); setMasivoResultado(null); setShowAccionesMasivas(true); }}>
                                        <MessageSquare size={12} /> WhatsApp masivo
                                    </button>
                                    <button className="leads-btn leads-btn--sm leads-btn--primary"
                                        onClick={() => { setAccionMasiva('dealer'); setMasivoDealerVal(''); setMasivoResultado(null); setShowAccionesMasivas(true); }}>
                                        <UserCheck size={12} /> Asignar dealer
                                    </button>
                                    <button className="leads-btn leads-btn--sm"
                                        onClick={() => { setAccionMasiva('estado'); setMasivoEstadoVal(''); setMasivoResultado(null); setShowAccionesMasivas(true); }}>
                                        <Tag size={12} /> Cambiar estado
                                    </button>
                                    <button className="leads-btn leads-btn--danger leads-btn--sm" onClick={eliminarSeleccionados}>
                                        <Trash2 size={12} /> Eliminar
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="leads-table-wrap">
                        {loading ? (
                            <div className="leads-empty"><div className="leads-spinner" /><p>Cargando leads...</p></div>
                        ) : leadsFiltrados.length === 0 ? (
                            <div className="leads-empty">
                                <div className="leads-empty-icon">👥</div>
                                <p style={{ fontWeight: 600 }}>Sin leads</p>
                                <p style={{ fontSize: 13 }}>Importa leads o ajusta los filtros</p>
                            </div>
                        ) : (
                            <table className="leads-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 36 }}><input type="checkbox" className="leads-checkbox" checked={selectedIds.size === leadsFiltrados.length && leadsFiltrados.length > 0} onChange={toggleSelectAll} /></th>
                                        <th>Contacto</th>
                                        <th>Modelo</th>
                                        <th>Presupuesto</th>
                                        <th>Región</th>
                                        <th>Fuente</th>
                                        <th>Estado</th>
                                        <th>Dealer</th>
                                        <th>WA</th>
                                        <th>Fecha</th>
                                        <th style={{ width: 40 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leadsFiltrados.map(lead => {
                                        const estado = ESTADOS.find(e => e.key === lead.estado) || ESTADOS[0];
                                        const waSent = (lead.notas || []).filter(n => n.tipo === 'whatsapp').length;
                                        const waRecv = (lead.notas || []).filter(n => n.tipo === 'whatsapp_recibido').length;
                                        const waCount = waSent + waRecv;
                                        return (
                                            <tr key={lead.id} className={selectedIds.has(lead.id) ? 'selected' : ''} onClick={() => setLeadActivo(lead)}>
                                                <td onClick={e => e.stopPropagation()}><input type="checkbox" className="leads-checkbox" checked={selectedIds.has(lead.id)} onChange={() => toggleSelect(lead.id)} /></td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg,#2563eb,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{initials(lead.nombre)}</div>
                                                        <div>
                                                            <div style={{ fontWeight: 600, fontSize: 13 }}>{lead.nombre || '-'}</div>
                                                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{lead.email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{lead.modelo || '-'}</td>
                                                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{lead.presupuesto ? clpFmt(lead.presupuesto) : '-'}</td>
                                                <td style={{ fontSize: 12, color: '#64748b' }}>{lead.region || '-'}</td>
                                                <td><span className="leads-fuente">{lead.fuente || '-'}</span></td>
                                                <td>
                                                    <span className={'leads-status leads-status--' + (lead.estado || 'nuevo')}>
                                                        <span className="leads-status-dot" />{estado.label}
                                                    </span>
                                                </td>
                                                <td style={{ fontSize: 12, color: lead.dealerAsignado ? '#0f172a' : '#d1d5db' }}>{lead.dealerAsignado || 'Sin asignar'}</td>
                                                <td>
                                                    {waCount > 0
                                                        ? <span style={{ fontSize: 11, background: '#dcfce7', color: '#16a34a', borderRadius: 999, padding: '1px 7px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                                            <MessageSquare size={10} /> {waSent}
                                                            {waRecv > 0 && <span style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: 999, padding: '0 4px', marginLeft: 2 }}>{waRecv} ↩</span>}
                                                        </span>
                                                        : <span style={{ color: '#e2e8f0', fontSize: 11 }}>—</span>
                                                    }
                                                </td>
                                                <td style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDate(lead.creadoEn)}</td>
                                                <td><ChevronRight size={14} style={{ color: '#cbd5e1' }} /></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                {/* Modal nuevo lead */}
                {showNuevoLead && (
                    <div className="leads-detail-overlay" onClick={() => { setShowNuevoLead(false); setNuevoLead({}); limpiarSeleccionCatalogo(); }}>
                        <div className="leads-detail-panel" onClick={e => e.stopPropagation()}>
                            <div className="leads-detail-header">
                                <div className="leads-detail-avatar" style={{ background: 'linear-gradient(135deg,#059669,#0891b2)' }}>+</div>
                                <div style={{ flex: 1 }}><div className="leads-detail-name">Nuevo lead</div><div style={{ fontSize: 12, color: '#94a3b8' }}>Ingreso manual</div></div>
                                <button className="leads-btn leads-btn--ghost leads-btn--sm" onClick={() => { setShowNuevoLead(false); setNuevoLead({}); limpiarSeleccionCatalogo(); }}><X size={16} /></button>
                            </div>
                            <div className="leads-detail-body">
                                <div className="leads-detail-section">
                                    <div className="leads-detail-section-title">Datos de contacto</div>
                                    {[
                                        { key: 'nombre', label: 'Nombre *', type: 'text', ph: 'Juan Pérez' },
                                        { key: 'email', label: 'Email *', type: 'email', ph: 'juan@ejemplo.com' },
                                        { key: 'telefono', label: 'Teléfono', type: 'text', ph: '+56 9 1234 5678' },
                                        { key: 'region', label: 'Región', type: 'text', ph: 'Metropolitana' },
                                    ].map(({ key, label, type, ph }) => (
                                        <div key={key} style={{ marginBottom: 10 }}>
                                            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>{label}</label>
                                            <input type={type} placeholder={ph} value={nuevoLead[key] || ''} onChange={e => setNuevoLead(p => ({ ...p, [key]: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                                        </div>
                                    ))}
                                </div>
                                <div className="leads-detail-section">
                                    <div className="leads-detail-section-title">Vehículo de interés</div>

                                    {/* Selector encadenado desde catálogo */}
                                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <Car size={12} /> Seleccionar del catálogo
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {/* Marca */}
                                            <div>
                                                <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 3 }}>Marca</label>
                                                <select
                                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', fontFamily: 'inherit' }}
                                                    value={catMarcaSel}
                                                    onChange={e => cargarModelosCat(e.target.value)}>
                                                    <option value="">— Selecciona marca —</option>
                                                    {catMarcas.map(m => <option key={m} value={m}>{m}</option>)}
                                                </select>
                                            </div>
                                            {/* Modelo */}
                                            {catMarcaSel && (
                                                <div>
                                                    <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 3 }}>
                                                        Modelo {catLoadingModelos && <span style={{ color: '#2563eb' }}>cargando…</span>}
                                                    </label>
                                                    <select
                                                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', fontFamily: 'inherit' }}
                                                        value={catModeloSel}
                                                        disabled={catLoadingModelos || !catModelos.length}
                                                        onChange={e => cargarVersionesCat(catMarcaSel, e.target.value)}>
                                                        <option value="">— Selecciona modelo —</option>
                                                        {catModelos.map(m => <option key={m} value={m}>{m}</option>)}
                                                    </select>
                                                </div>
                                            )}
                                            {/* Versión */}
                                            {catModeloSel && (
                                                <div>
                                                    <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 3 }}>
                                                        Versión {catLoadingVersiones && <span style={{ color: '#2563eb' }}>cargando…</span>}
                                                        <span style={{ color: '#cbd5e1', marginLeft: 6, fontWeight: 400 }}>(opcional)</span>
                                                    </label>
                                                    <select
                                                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', fontFamily: 'inherit' }}
                                                        value={catVersionSel}
                                                        disabled={catLoadingVersiones}
                                                        onChange={e => setCatVersionSel(e.target.value)}>
                                                        <option value="">— Todas las versiones —</option>
                                                        {catVersiones.map(v => <option key={v} value={v}>{v}</option>)}
                                                    </select>
                                                </div>
                                            )}
                                            {/* Botones */}
                                            {catModeloSel && (
                                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
                                                    <button
                                                        style={{ fontSize: 12, padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 7, background: '#fff', cursor: 'pointer', color: '#64748b', fontFamily: 'inherit' }}
                                                        onClick={limpiarSeleccionCatalogo}>
                                                        <X size={11} style={{ display: 'inline', marginRight: 3 }} /> Limpiar
                                                    </button>
                                                    <button
                                                        style={{ fontSize: 12, padding: '6px 14px', border: '1px solid #2563eb', borderRadius: 7, background: '#2563eb', cursor: 'pointer', color: '#fff', fontFamily: 'inherit', fontWeight: 600 }}
                                                        onClick={aplicarSeleccionCatalogo}>
                                                        <CheckCircle size={11} style={{ display: 'inline', marginRight: 3 }} /> Aplicar
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Campo texto — se llena con el selector o manualmente */}
                                    <div style={{ marginBottom: 10 }}>
                                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>
                                            Modelo de interés
                                            {nuevoLead.modelo && <span style={{ marginLeft: 6, fontSize: 10, color: '#2563eb', fontWeight: 400, textTransform: 'none' }}>del catálogo</span>}
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Se llena desde el catálogo o escribe manualmente"
                                            value={nuevoLead.modelo || ''}
                                            onChange={e => { setNuevoLead(p => ({ ...p, modelo: e.target.value })); limpiarSeleccionCatalogo(); }}
                                            style={{ width: '100%', padding: '8px 12px', border: `1px solid ${nuevoLead.modelo ? '#93c5fd' : '#e2e8f0'}`, background: nuevoLead.modelo ? '#eff6ff' : '#fff', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                                        />
                                    </div>

                                    {/* Presupuesto y concesionario */}
                                    {[
                                        { key: 'presupuesto', label: 'Presupuesto', ph: '25000000' },
                                        { key: 'concesionario', label: 'Concesionario', ph: 'Las Condes' },
                                    ].map(({ key, label, ph }) => (
                                        <div key={key} style={{ marginBottom: 10 }}>
                                            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>{label}</label>
                                            <input type="text" placeholder={ph} value={nuevoLead[key] || ''} onChange={e => setNuevoLead(p => ({ ...p, [key]: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                                        </div>
                                    ))}
                                    <div style={{ marginBottom: 10 }}>
                                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>Fuente</label>
                                        <select value={nuevoLead.fuente || ''} onChange={e => setNuevoLead(p => ({ ...p, fuente: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', fontFamily: 'inherit' }}>
                                            <option value="">Seleccionar fuente</option>
                                            {['Web', 'Facebook', 'Instagram', 'Google Ads', 'Referido', 'Showroom', 'Llamada', 'Email', 'Otro'].map(f => <option key={f} value={f}>{f}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 6 }}>Estado inicial</label>
                                        <div className="leads-status-selector">
                                            {ESTADOS.map(e => (
                                                <button key={e.key} className={'leads-status-btn' + ((nuevoLead.estado || 'nuevo') === e.key ? ' active' : '')} style={{ background: e.bg, color: e.color, borderColor: (nuevoLead.estado || 'nuevo') === e.key ? e.color : 'transparent' }} onClick={() => setNuevoLead(p => ({ ...p, estado: e.key }))}>{e.label}</button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="leads-detail-footer">
                                <button className="leads-btn" style={{ flex: 1 }} onClick={() => { setShowNuevoLead(false); setNuevoLead({}); limpiarSeleccionCatalogo(); }}>Cancelar</button>
                                <button className="leads-btn leads-btn--success" style={{ flex: 2 }} disabled={savingLead || (!nuevoLead.nombre && !nuevoLead.email)} onClick={guardarNuevoLead}><Plus size={14} /> {savingLead ? 'Guardando...' : 'Crear lead'}</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Modal acciones masivas ── */}
                {showAccionesMasivas && (
                    <div className="leads-detail-overlay" onClick={() => !masivoRunning && setShowAccionesMasivas(false)}>
                        <div className="leads-detail-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>

                            <div className="leads-detail-header">
                                <div className="leads-detail-avatar" style={{ background: 'linear-gradient(135deg,#2563eb,#16a34a)', fontSize: 13 }}>
                                    {selectedIds.size}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div className="leads-detail-name">
                                        {accionMasiva === 'whatsapp' && 'WhatsApp masivo'}
                                        {accionMasiva === 'dealer' && 'Asignar concesionario'}
                                        {accionMasiva === 'estado' && 'Cambiar estado'}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#94a3b8' }}>
                                        {selectedIds.size} leads seleccionados
                                    </div>
                                </div>
                                <button className="leads-btn leads-btn--ghost leads-btn--sm"
                                    onClick={() => setShowAccionesMasivas(false)} disabled={masivoRunning}>
                                    <X size={16} />
                                </button>
                            </div>

                            <div className="leads-detail-body">

                                {/* ── DEALER ── */}
                                {accionMasiva === 'dealer' && (
                                    <div className="leads-detail-section">
                                        <div className="leads-detail-section-title">Concesionario a asignar</div>
                                        <select className="leads-dealer-select" style={{ width: '100%', marginBottom: 12 }}
                                            value={masivoDealerVal} onChange={e => setMasivoDealerVal(e.target.value)}>
                                            <option value="">Sin asignar (remover asignación)</option>
                                            {dealers.length > 0
                                                ? dealers.map(d => <option key={d.id} value={d.nombre || d.id}>{d.nombre || d.id}</option>)
                                                : dealersUnicos.map(d => <option key={d} value={d}>{d}</option>)
                                            }
                                        </select>
                                        <div style={{ fontSize: 12, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
                                            Se asignará <strong>{masivoDealerVal || 'ningún concesionario'}</strong> a los {selectedIds.size} leads seleccionados.
                                        </div>
                                    </div>
                                )}

                                {/* ── ESTADO ── */}
                                {accionMasiva === 'estado' && (
                                    <div className="leads-detail-section">
                                        <div className="leads-detail-section-title">Nuevo estado</div>
                                        <div className="leads-status-selector" style={{ flexWrap: 'wrap', marginBottom: 12 }}>
                                            {ESTADOS.map(e => (
                                                <button key={e.key}
                                                    className={'leads-status-btn' + (masivoEstadoVal === e.key ? ' active' : '')}
                                                    style={{ background: e.bg, color: e.color, borderColor: masivoEstadoVal === e.key ? e.color : 'transparent' }}
                                                    onClick={() => setMasivoEstadoVal(e.key)}>
                                                    {e.label}
                                                </button>
                                            ))}
                                        </div>
                                        {masivoEstadoVal && (
                                            <div style={{ fontSize: 12, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
                                                Se cambiará el estado a <strong>{ESTADOS.find(e => e.key === masivoEstadoVal)?.label}</strong> en {selectedIds.size} leads.
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* ── WHATSAPP ── */}
                                {accionMasiva === 'whatsapp' && (
                                    <>
                                        <div className="leads-detail-section">
                                            <div className="leads-detail-section-title">Plantilla</div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                                                {WA_TEMPLATES.map(t => (
                                                    <button key={t.id} className="leads-template-btn"
                                                        style={{ background: masivoWaTemplate === t.id ? '#f0fdf4' : undefined, borderColor: masivoWaTemplate === t.id ? '#16a34a' : undefined }}
                                                        onClick={() => {
                                                            setMasivoWaTemplate(t.id);
                                                            // Preview con placeholder
                                                            const preview = { nombre: '{nombre}', modelo: '{modelo}', concesionario: '{concesionario}', presupuesto: '' };
                                                            setMasivoWaMensaje(t.texto(preview));
                                                        }}>
                                                        <div style={{ flex: 1, textAlign: 'left' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                <span style={{ fontWeight: 600, fontSize: 12, color: '#374151' }}>{t.label}</span>
                                                                {t.contentSid
                                                                    ? <span style={{ fontSize: 10, background: '#dcfce7', color: '#166534', borderRadius: 4, padding: '1px 5px', fontWeight: 600 }}>aprobado</span>
                                                                    : <span style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '1px 5px' }}>ventana 24h</span>
                                                                }
                                                            </div>
                                                        </div>
                                                        {masivoWaTemplate === t.id && <CheckCircle size={14} style={{ color: '#16a34a', flexShrink: 0 }} />}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="leads-detail-section">
                                            <div className="leads-detail-section-title">
                                                Mensaje
                                                <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginLeft: 8 }}>
                                                    usa {'{nombre}'} {'{modelo}'} {'{concesionario}'} para personalizar
                                                </span>
                                            </div>
                                            <textarea
                                                className="leads-nota-input"
                                                placeholder="Escribe el mensaje... usa {nombre}, {modelo}, {concesionario} para personalizar"
                                                value={masivoWaMensaje}
                                                onChange={e => setMasivoWaMensaje(e.target.value)}
                                                style={{ minHeight: 100 }}
                                            />
                                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, display: 'flex', gap: 12 }}>
                                                <span>{masivoWaMensaje.length} caracteres</span>
                                                <span>·</span>
                                                <span>{leads.filter(l => selectedIds.has(l.id) && normalizePhone(l.telefono)).length} de {selectedIds.size} leads tienen teléfono</span>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Resultado */}
                                {masivoResultado && (
                                    <div style={{
                                        padding: '10px 14px', borderRadius: 8, fontSize: 13,
                                        background: masivoResultado.err === 0 ? '#f0fdf4' : '#fef2f2',
                                        border: `1px solid ${masivoResultado.err === 0 ? '#bbf7d0' : '#fecaca'}`,
                                        color: masivoResultado.err === 0 ? '#166534' : '#991b1b',
                                    }}>
                                        {masivoResultado.err === 0
                                            ? <><CheckCircle size={14} style={{ display: 'inline', marginRight: 6 }} />{masivoResultado.ok} leads actualizados correctamente{masivoResultado.omitidos > 0 ? ` · ${masivoResultado.omitidos} omitidos (sin teléfono)` : ''}</>
                                            : <><AlertCircle size={14} style={{ display: 'inline', marginRight: 6 }} />{masivoResultado.ok} OK · {masivoResultado.err} errores · {masivoResultado.omitidos} sin teléfono</>
                                        }
                                    </div>
                                )}

                                {/* Progress indicator */}
                                {masivoRunning && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, fontSize: 13, color: '#64748b' }}>
                                        <div className="leads-spinner" style={{ width: 16, height: 16 }} />
                                        Procesando leads... no cierres esta ventana
                                    </div>
                                )}
                            </div>

                            <div className="leads-detail-footer">
                                <button className="leads-btn" style={{ flex: 1 }}
                                    onClick={() => setShowAccionesMasivas(false)} disabled={masivoRunning}>
                                    {masivoResultado ? 'Cerrar' : 'Cancelar'}
                                </button>
                                {!masivoResultado && (
                                    <button className="leads-btn leads-btn--primary" style={{ flex: 2 }}
                                        disabled={masivoRunning ||
                                            (accionMasiva === 'whatsapp' && !masivoWaMensaje.trim()) ||
                                            (accionMasiva === 'estado' && !masivoEstadoVal)
                                        }
                                        onClick={ejecutarAccionMasiva}>
                                        {masivoRunning ? 'Procesando…' : (
                                            accionMasiva === 'whatsapp' ? <><Send size={13} /> Enviar a {leads.filter(l => selectedIds.has(l.id) && normalizePhone(l.telefono)).length} leads</> :
                                                accionMasiva === 'dealer' ? <><UserCheck size={13} /> Asignar a {selectedIds.size} leads</> :
                                                    <><Tag size={13} /> Aplicar a {selectedIds.size} leads</>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Panel detalle */}
                {leadActivo && (
                    <LeadDetailPanel
                        lead={leadActivo}
                        dealers={dealers}
                        dealersUnicos={dealersUnicos}
                        onClose={() => setLeadActivo(null)}
                        onUpdate={onLeadUpdate}
                    />
                )}
            </div>
        </div>
    );

    if (embedded) return innerContent;
    return <div className="layout"><Sidebar />{innerContent}</div>;
}

export default GestionLeads;